<template>
  <v-container style="position: relative;">
    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; flex-wrap: wrap; gap: 16px;">
      <div style="flex: 1; min-width: 200px; display: flex; align-items: center; gap: 8px;">
        <!-- Mute/unmute button for read-only viewers -->
        <v-btn
          icon
          @click="soundEnabled = !soundEnabled"
          :title="soundEnabled ? 'Mute sounds' : 'Unmute sounds'"
          :color="soundEnabled ? 'primary' : 'grey'"
          style="margin-left: 8px;"
        >
          <v-icon>{{ soundEnabled ? 'mdi-volume-high' : 'mdi-volume-off' }}</v-icon>
        </v-btn>
      </div> <!-- Left spacer -->
      <h1 style="flex: 1; text-align: center;">{{ tournamentName }}
        <v-btn icon @click="uiStore.toggleNavBar()" :title="uiStore.navBarVisible ? 'Hide Menu' : 'Show Menu'">
          <v-icon>{{ uiStore.navBarVisible ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
        </v-btn>
      </h1>
      <div style="flex: 1; display: flex; justify-content: flex-end; align-items: center; gap: 8px; min-width: 200px;">
        <input v-model="idInput" placeholder="Enter tournament id to view"
          style="padding: 4px 8px; font-size: 1rem; border-radius: 4px; border: 1px solid #ccc;" @keyup.enter="goToId" />
        <v-btn color="primary" @click="goToId" :disabled="!idInput">View</v-btn>
        <!-- (switch removed; mute button moved to left header) -->
        <!-- Wake Lock API support indicator (read-only view will not acquire a lock) -->
        <v-btn icon :title="supportedWakeLock ? 'Wake Lock API supported (read-only view will not acquire lock)' : 'Wake Lock API not supported'" :color="supportedWakeLock ? 'info' : 'grey'">
          <v-icon>{{ supportedWakeLock ? 'mdi-lock-alert' : 'mdi-lock-open-variant' }}</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Main Display from Shared Component -->
    <TournamentDisplay :timer-state="timerState" :level-state="levelState" :payout-state="payoutState"
      :summary-state="summaryState" :announcement="announcement" :display-config="displayConfig"
      :rebuy-end-level="rebuyEndLevel" />

    <!-- Summary from Shared Component -->
    <TournamentSummary :players-left="playersLeft" :total-players="totalPlayers" :total-rebuys="totalRebuys" :busted-player-count="bustedPlayerCount"
      :track-players-by-name="trackPlayersByName"
      :total-add-ons="totalAddOns" :rebuy-allowed="rebuyAllowed" :addon-allowed="addonAllowed" />

    <div v-if="nextBreakLevel" class="next-break-notice">Next break after Level {{ nextBreakLevel }}</div>
    <!-- Chip Denominations Visualizer -->
    <div v-if="chipDenominations.length > 0" class="chip-display-container" style="display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 16px; margin: 8px auto 24px; padding: 16px; background-color: #f0f0f0; border-radius: 8px; max-width: 800px;">
      <div v-for="(chip, index) in chipDenominations" :key="`vis-${index}`" class="chip-visual" :style="{ backgroundColor: chip.color, borderColor: chip.accentColor }">
        <div class="chip-value" :style="{ color: getContrastingTextColor(chip.color) }">
          {{ chip.value }}
        </div>
      </div>
    </div>
    <!-- Audio elements for sound alerts -->
    <audio ref="oneMinuteWarningSound" src="/sounds/one_minute_warning.mp3" preload="auto"></audio>
    <audio ref="levelEndSound" src="/sounds/level_end.mp3" preload="auto"></audio>
    <!-- Shown only if the browser (iPad Safari) blocked a sound; any tap re-enables it (see useAudioUnlock) -->
    <v-alert v-if="showSilentHint" type="info" variant="tonal" density="compact" closable @click:close="dismissSilentHint" style="position: fixed; bottom: 90px; left: 16px; right: 16px; z-index: 3000;">
      Timer sounds are muted when this iPad is in silent mode. Turn silent mode off (the bell in Control Center) to hear them.
    </v-alert>
    <v-btn v-if="soundBlocked" color="warning" prepend-icon="mdi-volume-high" style="position: fixed; bottom: 90px; right: 16px; z-index: 3000;">Tap to enable sound</v-btn>

    <!-- Knockout notification -->
    <v-snackbar v-model="knockoutSnackbar" :timeout="3000" location="top">
      {{ knockoutSnackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { db } from '@/firebaseConfig';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import {
  TOURNAMENT_SETTINGS_DB,
  TOURNAMENT_PLAYERS_DB,
  TOURNAMENT_PAYOUTS_DB,
  TOURNAMENT_TIMERS_DB
} from '@/constants/constants';
import TournamentSummary from '@/components/custom/TournamentSummary.vue';
import { useUiStore } from '@/stores/ui';
import TournamentDisplay from '@/components/custom/TournamentDisplay.vue';
import { useRouter } from 'vue-router';
import { mergeDisplayConfig } from '@/utils/displayConfig';
import { useAudioUnlock } from '@/compostables/useAudioUnlock';

// Accept id as a prop (from route)
const props = defineProps({ id: { type: String, default: 'public' } });

const uiStore = useUiStore();
const router = useRouter();
const idInput = ref(props.id || '');
const tournamentName = ref('');

const timeLeft = ref(null);
const level = ref(1);
const normalLevelCount = ref(1); // Track normal levels separately
const levels = ref([]);
const players = ref([]);
const bustedPlayers = ref([]);
const buyinAmount = ref(0);
const rebuyAmount = ref(0);
const addonAmount = ref(0);
const rebuyAllowed = ref(false);
const rebuyEndLevel = ref(null);
const addonAllowed = ref(false);
const buyinChips = ref(1000);
const addonChips = ref(1000);
const trackPlayersByName = ref(false);
const chipDenominations = ref([]);
const announcement = ref(null);
const displayConfig = ref(mergeDisplayConfig());

// Manual count refs
const manualTotalPlayers = ref(0);
const manualTotalRebuys = ref(0);
const manualTotalAddOns = ref(0);
const bustedPlayerCount = ref(0);

// Knockout notification
const knockoutSnackbar = ref(false);
const knockoutSnackbarText = ref('');
let prevBustedPlayerIds = null; // null = not yet initialized (first load)

const currentLevelData = computed(() => {
  if (!levels.value || levels.value.length === 0) return null;
  const idx = Math.max(0, Math.min(level.value - 1, levels.value.length - 1));
  return levels.value[idx];
});

const isCurrentLevelBreak = computed(() => {
  return currentLevelData.value?.isBreak || false;
});

// Level number (in the same "Level N" numbering shown on-screen, which skips breaks)
// after which the next break occurs, or null if no break is upcoming.
const nextBreakLevel = computed(() => {
  if (!levels.value || levels.value.length === 0 || isCurrentLevelBreak.value) return null;
  let idx = level.value;
  while (idx < levels.value.length && !levels.value[idx].isBreak) {
    idx++;
  }
  if (idx >= levels.value.length) return null;
  let count = 0;
  for (let i = 0; i < idx; i++) {
    if (!levels.value[i].isBreak) count++;
  }
  return count;
});

// Add nextLevelData computed property - skip breaks to find next non-break level
const nextLevelData = computed(() => {
  if (!levels.value || levels.value.length === 0) return null;
  // Start from the next level index
  let idx = level.value;
  // Skip breaks to find the next non-break level
  while (idx < levels.value.length && levels.value[idx]?.isBreak) {
    idx++;
  }
  // If we found a non-break level, return it
  if (idx < levels.value.length) {
    return levels.value[idx];
  }
  // If no more levels, return null
  return null;
});

const playersLeft = computed(() => {
  if (trackPlayersByName.value) {
    let totalEntries = 0;
    players.value.forEach(player => {
      totalEntries += 1 + (player.rebuys || 0); // Original entry + rebuys
    });
    return totalEntries - bustedPlayers.value.length;
  }
  return (manualTotalPlayers.value + manualTotalRebuys.value) - bustedPlayerCount.value;
});

const totalPlayers = computed(() => {
  return trackPlayersByName.value ? players.value.length : manualTotalPlayers.value;
});

const totalRebuys = computed(() => {
  if (trackPlayersByName.value) {
    return players.value.reduce((total, player) => total + (player.rebuys || 0), 0);
  }
  return manualTotalRebuys.value;
});

const totalAddOns = computed(() => {
  if (trackPlayersByName.value) {
    return players.value.reduce((total, player) => total + (player.addOn ? 1 : 0), 0);
  }
  return manualTotalAddOns.value;
});

// Sound refs
const oneMinuteWarningSound = ref(null);
const levelEndSound = ref(null);
// iPads block timer-driven play() until each element has been played from a tap; unlock on first tap
const { playSound, soundBlocked, showSilentHint, dismissSilentHint } = useAudioUnlock(() => [oneMinuteWarningSound.value, levelEndSound.value]);
// Allow read-only viewers to mute sounds (persisted in localStorage)
const soundEnabled = ref(true);

const totalPot = computed(() => {
  let pot = totalPlayers.value * buyinAmount.value;
  if (rebuyAllowed.value) { pot += totalRebuys.value * rebuyAmount.value; }
  if (addonAllowed.value) { pot += totalAddOns.value * addonAmount.value; }
  return pot;
});
const totalChips = computed(() => {
  let chips = totalPlayers.value * buyinChips.value;
  if (rebuyAllowed.value) {
    chips += totalRebuys.value * buyinChips.value;
  }
  if (addonAllowed.value) {
    chips += totalAddOns.value * addonChips.value;
  }
  return chips;
});
const averageChips = computed(() => {
  if (playersLeft.value === 0) return 0;
  return Math.round(totalChips.value / playersLeft.value);
});
const formattedTime = computed(() => {
  if (timeLeft.value === null) return '--:--';
  const minutes = Math.floor(timeLeft.value / 60).toString().padStart(2, '0');
  const seconds = (timeLeft.value % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
});

const timerState = computed(() => ({
  formattedTime: formattedTime.value,
  isCurrentLevelBreak: isCurrentLevelBreak.value,
  normalLevelCount: normalLevelCount.value,
}));

const levelState = computed(() => ({
  currentLevelData: currentLevelData.value,
  nextLevelData: nextLevelData.value,
}));

const payoutState = computed(() => ({
  visiblePayouts: visiblePayouts.value,
  numberOfPayouts: numberOfPayouts.value,
  totalPrizePool: totalPrizePool.value,
}));

const summaryState = computed(() => ({
  totalPot: totalPot.value,
  averageChips: averageChips.value,
  totalPlayers: trackPlayersByName.value
    ? totalPlayers.value
    : totalPlayers.value + totalRebuys.value,
}));

const numberOfPayouts = ref(3);
const payouts = ref([]);
const totalPrizePool = computed(() => totalPot.value);
const payoutScrollIndex = ref(0);
let payoutScrollInterval = null;
const visiblePayouts = computed(() => {
  if (!payouts.value || payouts.value.length <= 5) return payouts.value;
  const start = payoutScrollIndex.value;
  const end = start + 5;
  if (end <= payouts.value.length) {
    return payouts.value.slice(start, end);
  } else {
    return [
      ...payouts.value.slice(start),
      ...payouts.value.slice(0, end - payouts.value.length)
    ];
  }
});

function startPayoutScroll() {
  if (payoutScrollInterval) return;
  payoutScrollInterval = setInterval(() => {
    if (!payouts.value || payouts.value.length <= 5) return;
    payoutScrollIndex.value = (payoutScrollIndex.value + 1) % payouts.value.length;
    if (payoutScrollIndex.value > payouts.value.length - 5) {
      payoutScrollIndex.value = 0;
    }
  }, 3000);
}
function stopPayoutScroll() {
  if (payoutScrollInterval) {
    clearInterval(payoutScrollInterval);
    payoutScrollInterval = null;
  }
}

function goToId() {
  if (idInput.value) {
    router.push({ name: 'TournamentViewReadOnly', params: { id: idInput.value } });
  }
}

function getContrastingTextColor(hexcolor){
  if (!hexcolor) return '#000000';
  // If a leading # is provided, remove it
  if (hexcolor.slice(0, 1) === '#') {
    hexcolor = hexcolor.slice(1);
  }

  // If a three-character hexcode, convert to six
  if (hexcolor.length === 3) {
    hexcolor = hexcolor.split('').map(function (hex) {
      return hex + hex;
    }).join('');
  }

  const r = parseInt(hexcolor.substr(0,2),16);
  const g = parseInt(hexcolor.substr(2,2),16);
  const b = parseInt(hexcolor.substr(4,2),16);
  const yiq = ((r*299)+(g*587)+(b*114))/1000;
  return (yiq >= 128) ? '#000000' : '#FFFFFF';
}


const timerRunning = ref(false);
const countdownDuration = ref(0);
const supportedWakeLock = ref(false);

let settingsUnsub = null;
let playersUnsub = null;
let payoutsUnsub = null;
let timerUnsub = null;
let localTimerInterval = null;
let baseEndTime = null;

function getDurationForLevelIndex(index) {
  const levelData = levels.value[index];
  if (!levelData) {
    return countdownDuration.value || 0;
  }
  if (typeof levelData.duration === 'number' && levelData.duration > 0) {
    return levelData.duration * 60;
  }
  return countdownDuration.value || 0;
}

function scheduleLocalCountdown(durationSeconds, baseTime = Date.now()) {
  const duration = durationSeconds > 0 ? durationSeconds : countdownDuration.value || 0;
  countdownDuration.value = duration;
  baseEndTime = baseTime + (duration * 1000);
  timeLeft.value = Math.max(0, Math.round((baseEndTime - Date.now()) / 1000));  
}

function advanceLocalLevelUsingPreviousEnd() {
  const nextLevelIndex = level.value;
  if (!levels.value || nextLevelIndex >= levels.value.length) {
    return false;
  }

  const duration = getDurationForLevelIndex(nextLevelIndex);
  const previousEnd = baseEndTime ?? Date.now();
  level.value++;

  const previousLevelData = levels.value[level.value - 2];
  if (previousLevelData && !previousLevelData.isBreak) {
    normalLevelCount.value = Math.min(normalLevelCount.value + 1, levels.value.length);
  }

  scheduleLocalCountdown(duration, previousEnd);
  return true;
}

function handleLocalLevelTransitions(now = Date.now()) {
  let transitioned = false;
  while (timerRunning.value && baseEndTime && now >= baseEndTime) {
    if (soundEnabled.value) playSound(levelEndSound.value);
    const advanced = advanceLocalLevelUsingPreviousEnd();
    if (!advanced) break;
    transitioned = true;
  }

  if (transitioned && timerRunning.value && baseEndTime) {
    timeLeft.value = Math.max(0, Math.round((baseEndTime - Date.now()) / 1000));
  }
}

function startLocalTimer() {
  stopLocalTimer();
  localTimerInterval = setInterval(() => {
    if (!timerRunning.value || !baseEndTime) return;
    const now = Date.now();
    const oldTimeLeft = timeLeft.value;

    if (now >= baseEndTime) {
      handleLocalLevelTransitions(now);
    } else {
      timeLeft.value = Math.max(0, Math.round((baseEndTime - now) / 1000));
    }

    // Play one-minute warning sound
    if (oldTimeLeft > 60 && timeLeft.value <= 60 && soundEnabled.value) {
      playSound(oneMinuteWarningSound.value);
    }
  }, 1000);
}

function stopLocalTimer() {
  if (localTimerInterval) {
    clearInterval(localTimerInterval);
    localTimerInterval = null;
  }
}

function cleanupListeners() {
  if (settingsUnsub) { settingsUnsub(); settingsUnsub = null; }
  if (playersUnsub) { playersUnsub(); playersUnsub = null; }
  if (payoutsUnsub) { payoutsUnsub(); payoutsUnsub = null; }
  if (timerUnsub) { timerUnsub(); timerUnsub = null; }
  stopLocalTimer();
  prevBustedPlayerIds = null;
}

async function setupListeners(viewingCode) {
  cleanupListeners();

  const trimmed = (viewingCode || '').trim();
  if (!trimmed || trimmed === 'public') {
    console.warn('No viewing code provided.');
    return;
  }

  try {
    const settingsCol = collection(db, TOURNAMENT_SETTINGS_DB);
    const codeNormalized = trimmed.toLowerCase();
    let q = query(settingsCol, where('codeNameLower', '==', codeNormalized));
    let querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      q = query(settingsCol, where('codeName', '==', (viewingCode || '').trim()));
      querySnapshot = await getDocs(q);
    }

    if (querySnapshot.empty) {
      console.warn('No tournament found for codeName:', viewingCode);
      return;
    }

    const settingsDocSnap = querySnapshot.docs[0];
    const tournamentId = settingsDocSnap.id;
    tournamentName.value = settingsDocSnap.data().tournamentName || 'Tournament View (Read Only)';

    // Now use tournamentName to set up listeners
    const settingsDocRef = doc(db, TOURNAMENT_SETTINGS_DB, tournamentId);
    const playersDocRef = doc(db, TOURNAMENT_PLAYERS_DB, tournamentId);
    const payoutsDocRef = doc(db, TOURNAMENT_PAYOUTS_DB, tournamentId);
    const timerDocRef = doc(db, TOURNAMENT_TIMERS_DB, tournamentId);

    settingsUnsub = onSnapshot(settingsDocRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      buyinAmount.value = data.buyinAmount || 0;
      rebuyAmount.value = data.rebuyAmount || 0;
      addonAmount.value = data.addonAmount || 0;
      rebuyAllowed.value = data.rebuyAllowed || false;
      if (typeof data.rebuyEndLevel === 'number' || data.rebuyEndLevel === null) {
        rebuyEndLevel.value = data.rebuyEndLevel ?? null;
      }
      addonAllowed.value = data.addonAllowed || false;
      buyinChips.value = data.buyinChips || 1000;
      addonChips.value = data.addonChips || 1000;
      levels.value = data.levels || [];
      trackPlayersByName.value = data.trackPlayersByName ?? false;
      chipDenominations.value = data.chipDenominations || [];
      announcement.value = data.announcement?.text ? { text: data.announcement.text } : null;
      displayConfig.value = mergeDisplayConfig(data.displayConfig);
    });

    playersUnsub = onSnapshot(playersDocRef, (snap) => {
      if (!snap.exists()) {
        players.value = [];
        bustedPlayers.value = [];
        prevBustedPlayerIds = null;
        return;
      }
      const data = snap.data();
      const newPlayers = data.players || [];
      players.value = newPlayers;
      if (trackPlayersByName.value) {
        const newBusted = data.bustedPlayers || [];
        if (prevBustedPlayerIds === null) {
          // First load — record known bust IDs without showing notifications
          prevBustedPlayerIds = new Set(newBusted.map(bp => String(bp.playerId)));
        } else {
          for (const bust of newBusted) {
            if (!prevBustedPlayerIds.has(String(bust.playerId))) {
              const originalId = String(bust.playerId).includes('_rebuy_')
                ? String(bust.playerId).split('_rebuy_')[0]
                : String(bust.playerId);
              const bustedPlayer = newPlayers.find(p => String(p.id) === originalId);
              const playerName = bustedPlayer?.name || 'A player';
              knockoutSnackbarText.value = bust.knockedOutBy
                ? `${playerName} has been knocked out by ${bust.knockedOutBy}`
                : `${playerName} has been knocked out`;
              knockoutSnackbar.value = true;
              prevBustedPlayerIds.add(String(bust.playerId));
            }
          }
        }
        bustedPlayers.value = newBusted;
      } else {
        manualTotalPlayers.value = data.totalPlayers || 0;
        manualTotalRebuys.value = data.totalRebuys || 0;
        manualTotalAddOns.value = data.totalAddOns || 0;
        bustedPlayerCount.value = data.bustedPlayerCount || 0;
        prevBustedPlayerIds = null;
      }
    });

    payoutsUnsub = onSnapshot(payoutsDocRef, (snap) => {
      if (!snap.exists()) {
        payouts.value = [];
        numberOfPayouts.value = 0;
        return;
      }
      const data = snap.data();
      payouts.value = data.payouts || [];
      numberOfPayouts.value = data.numberOfPayouts || 0;
    });

    timerUnsub = onSnapshot(timerDocRef, (snap) => {
      if (!snap.exists()) {
        stopLocalTimer();
        timeLeft.value = null;
        level.value = 1;
        return;
      }
      const state = snap.data();
      level.value = state.level || 1;
      normalLevelCount.value = state.normalLevelCount || 1;
      countdownDuration.value = state.countdownDuration || 0;
      timerRunning.value = state.timerRunning || false;
      if (state.timerRunning) {
        // Prefer server-clock anchor to eliminate host clock skew.
        // savedAt is null on local-cache snapshots (hasPendingWrites: true); wait for server confirmation.
        if (state.savedAt && !snap.metadata.hasPendingWrites) {
          baseEndTime = state.savedAt.toMillis() + (state.timeLeft ?? 0) * 1000;
        } else if (state.endTime) {
          // Fallback: legacy host-clock endTime (backward compat / pending write)
          baseEndTime = state.endTime;
        }
        if (baseEndTime) {
          timeLeft.value = Math.max(0, Math.round((baseEndTime - Date.now()) / 1000));
          startLocalTimer();
        }
      } else {
        stopLocalTimer();
        timeLeft.value = state.timeLeft ?? 0;
        baseEndTime = null;
      }
    });
  } catch (error) {
    console.error('Error setting up listeners for codeName:', viewingCode, error);
  }
}

onMounted(() => {
  try {
    supportedWakeLock.value = ('wakeLock' in navigator);
  } catch (e) {
    supportedWakeLock.value = false;
  }
  // Load persisted sound preference for read-only viewers
  try {
    const stored = localStorage.getItem('readOnlySoundEnabled');
    if (stored !== null) soundEnabled.value = stored === 'true';
  } catch (e) {
    console.debug('localStorage read failed for readOnlySoundEnabled', e);
  }
  setupListeners(props.id || 'public');
});

// Persist sound preference when changed
watch(soundEnabled, (val) => {
  try {
    localStorage.setItem('readOnlySoundEnabled', val ? 'true' : 'false');
  } catch (e) {
    console.debug('localStorage write failed for readOnlySoundEnabled', e);
  }
});

watch(() => props.id, (newId) => {
  setupListeners(newId || 'public');
});

onBeforeUnmount(() => {
  cleanupListeners();
});

watch(payouts, (newVal) => {
  if (newVal && newVal.length > 5) {
    startPayoutScroll();
  } else {
    stopPayoutScroll();
    payoutScrollIndex.value = 0;
  }
});
</script>

<style scoped>
.next-break-notice {
  text-align: center;
  font-weight: 600;
  color: #555;
  margin: 24px auto 0;
}
</style>